import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import { CompanyUserWithUserAndRole } from '@shared/types/companies.types'
import { t } from 'i18next'
import { useExpelUserCompanyMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'

export const ExpelMemberFromCompanyModal = NiceModal.create(
  ({
    companyId,
    user,
    hasMoreFullAccessUsers
  }: {
    companyId: string
    user: CompanyUserWithUserAndRole
    hasMoreFullAccessUsers: boolean
  }) => {
    const modal = useModal()
    const { showSnackbar } = useSnackbar()

    const deleteUserCompanyMutation = useExpelUserCompanyMutation(companyId)

    const handleDelete = () => {
      deleteUserCompanyMutation.mutate(
        {
          userId: user.user.id
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: t('expel_member_from_company_modal.success'),
              severity: 'success'
            })
          },
          onError: () => {
            showSnackbar({
              message: t('expel_member_from_company_modal.error'),
              severity: 'error'
            })
          },
          onSettled: () => {
            modal.remove()
          }
        }
      )
    }

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '37.5rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ py: '2rem' }}>
          <Typography fontWeight='bold' variant='h6' component='span'>
            {t('expel_member_from_company_modal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack direction='column' spacing='1rem'>
            <Typography variant='body1' sx={{ mb: '1.5rem' }}>
              {t('expel_member_from_company_modal.text', {
                memberName: user.user.name
                  ? `${user.user.name} ${user.user.lastName}`
                  : ' '
              })}
            </Typography>
            {!hasMoreFullAccessUsers && (
              <Alert severity='error' sx={{ mt: '1rem' }}>
                <AlertTitle>
                  {t('expel_member_from_company_modal.alert_title')}
                </AlertTitle>
                {t('expel_member_from_company_modal.alert_text')}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => modal.remove()}
            sx={{ color: 'main.primary', textTransform: 'capitalize' }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant='contained'
            color='error'
            disabled={!hasMoreFullAccessUsers}
            onClick={handleDelete}
            sx={{ textTransform: 'capitalize' }}
          >
            {deleteUserCompanyMutation.isPending
              ? t('expel_member_from_company_modal.expeling')
              : t('expel_member_from_company_modal.expel_button')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)
